/* global G */
import { useCallback, useContext, useMemo } from 'react'
import AppContext from 'platform/react/context/application'
import { _aclProxyHandler } from 'lib/sequence/app/init/context'
import routeComposition from 'trait/composition/route'
import SelectField from 'ui/Element/Field/Select'
import { useMemoRef } from 'platform/react/hook'

/**
 * If the user has more than one context available, displays a select element to switch between
 * them.
 *
 * @param {Object} props          element properties
 * @param {Object} props.classes  styles coming from parent component
 * @returns {null|JSX.Element}
 */
const ContextSelect = ({ classes }) => {
  const { session, intl, router } = useContext(AppContext)
  const language = session[G.LANGUAGE]
  const meta = session[G.META]
  const { context } = meta

  const keys = Object.keys(context)
  const hidden = keys.length < 2

  const options = useMemo(() => Object.keys(context).map((key) => {
    const _key = `contexts.${key}`
    const value = intl._t(_key, { ns: 'custom', _key, defaultValue: key })
    return { key, value }
  }), [language, context])

  /**
   * Switches to the context whose name is expected to be passed as `event.detail.value`.
   *
   * @type {(function(PlatformEvent): Promise<void>)|*}
   */
  const onChange = useCallback(async (event) => {
    const newContextName = event.detail.value
    router.context(newContextName)

    const newContext = context[newContextName]
    meta.acl = new Proxy(newContext.acl, _aclProxyHandler)
    meta.roles = newContext.roles

    const defaultRoute = router.defaultRoute()
    const route = routeComposition(defaultRoute.module, defaultRoute.action)

    await router.moduleAction(route)
  }, [meta, context])

  return hidden ? null : (
    <SelectField
      key={'context'}
      options={options}
      value={session[G.CONTEXT]}
      events={{ onChange }}
      className={classes.contextSelect}
      helperText={null}
    />
  )
}

export default useMemoRef(ContextSelect, props => [props.classes])
